import React, { Component } from 'react';
import PropTypes from 'prop-types';
import '../SlateTextInput/SlateTextInput.css';

/**
 * Parses various time formats and returns { hours, minutes, period }
 * Supports: "9:30 AM", "09:30", "9:30am", "14:00", "2:00 PM", etc.
 */
function parseTimeString(timeStr) {
  if (!timeStr) return null;

  var str = timeStr.trim().toUpperCase();
  var period = null;

  // Check for AM/PM
  if (str.indexOf('AM') !== -1) {
    period = 'AM';
    str = str.replace(/\s*AM\s*/i, '');
  } else if (str.indexOf('PM') !== -1) {
    period = 'PM';
    str = str.replace(/\s*PM\s*/i, '');
  }

  // Parse hours and minutes
  var parts = str.split(':');
  if (parts.length < 2) return null;

  var hours = parseInt(parts[0], 10);
  var minutes = parseInt(parts[1], 10);

  if (isNaN(hours) || isNaN(minutes)) return null;
  if (minutes < 0 || minutes > 59) return null;

  // Convert to 24-hour if we have AM/PM
  if (period) {
    if (hours < 1 || hours > 12) return null;
    if (period === 'AM' && hours === 12) {
      hours = 0;
    } else if (period === 'PM' && hours !== 12) {
      hours = hours + 12;
    }
  } else {
    if (hours < 0 || hours > 23) return null;
  }

  return { hours: hours, minutes: minutes };
}

/**
 * Formats hours and minutes to 12-hour display string
 */
function formatTo12Hour(hours, minutes) {
  var period = hours >= 12 ? 'PM' : 'AM';
  var displayHours = hours % 12;
  if (displayHours === 0) displayHours = 12;
  var displayMinutes = minutes < 10 ? '0' + minutes : String(minutes);
  return displayHours + ':' + displayMinutes + ' ' + period;
}

/**
 * Formats hours and minutes to 24-hour string (HH:MM:SS)
 */
function formatTo24Hour(hours, minutes) {
  var displayHours = hours < 10 ? '0' + hours : String(hours);
  var displayMinutes = minutes < 10 ? '0' + minutes : String(minutes);
  return displayHours + ':' + displayMinutes + ':00';
}

class SlateTimeInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFocused: false,
      displayValue: this.getDisplayValue(props.value),
    };
    this.inputRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && !this.state.isFocused) {
      this.setState({ displayValue: this.getDisplayValue(this.props.value) });
    }
  }

  getDisplayValue = function(value) {
    if (!value) return '';
    // Value comes in as HH:MM:SS format
    var parts = value.split(':');
    if (parts.length < 2) return value;
    var hours = parseInt(parts[0], 10);
    var minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return value;
    return formatTo12Hour(hours, minutes);
  };

  handleFocus = function(e) {
    this.setState({ isFocused: true });
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  }.bind(this);

  handleBlur = function(e) {
    this.setState({ isFocused: false });

    // Parse and validate the input
    var parsed = parseTimeString(this.state.displayValue);

    if (parsed) {
      // Valid time - format for display and call onChange with 24-hour format
      var displayValue = formatTo12Hour(parsed.hours, parsed.minutes);
      var value24 = formatTo24Hour(parsed.hours, parsed.minutes);
      this.setState({ displayValue: displayValue });
      if (this.props.onChange) {
        this.props.onChange(value24);
      }
    } else if (this.state.displayValue === '') {
      // Empty is allowed
      if (this.props.onChange) {
        this.props.onChange('');
      }
    } else {
      // Invalid - revert to previous value
      this.setState({ displayValue: this.getDisplayValue(this.props.value) });
    }

    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  }.bind(this);

  handleChange = function(e) {
    this.setState({ displayValue: e.target.value });
  }.bind(this);

  hasValue = function() {
    return this.state.displayValue !== '';
  };

  focus = function() {
    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  };

  render() {
    var props = this.props;
    var name = props.name;
    var placeholder = props.placeholder;
    var label = props.label;
    var disabled = props.disabled;
    var error = props.error;
    var fullWidth = props.fullWidth;
    var autoFocus = props.autoFocus;
    var hint = props.hint;
    var className = props.className;
    var inputStyle = props.inputStyle;
    var containerStyle = props.containerStyle;

    var state = this.state;
    var isFocused = state.isFocused;
    var displayValue = state.displayValue;

    var hasValue = this.hasValue();
    var isLabelFloating = isFocused || hasValue;
    var hasLabel = label !== null && label !== undefined && label !== '';

    // Build container class names
    var containerClasses = [
      'slate-text-input',
      fullWidth ? 'slate-text-input--full-width' : '',
      disabled ? 'slate-text-input--disabled' : '',
      error ? 'slate-text-input--error' : '',
      isFocused ? 'slate-text-input--focused' : '',
      'slate-text-input--has-icon',
      !hasLabel ? 'slate-text-input--no-label' : '',
      className || '',
    ].filter(Boolean).join(' ');

    // Build label class names
    var labelClasses = [
      'slate-text-input-label',
      isLabelFloating ? 'slate-text-input-label--floating' : '',
    ].filter(Boolean).join(' ');

    return (
      <div className={containerClasses} style={containerStyle}>
        <div className="slate-text-input-wrapper">
          <span className="material-symbols-outlined slate-text-input-icon">
            schedule
          </span>

          {hasLabel && (
            <label className={labelClasses} htmlFor={name}>
              {label}
            </label>
          )}

          <input
            ref={this.inputRef}
            id={name}
            name={name}
            type="text"
            value={displayValue}
            placeholder={isLabelFloating || !hasLabel ? (placeholder || '12:00 PM') : ''}
            disabled={disabled}
            autoFocus={autoFocus}
            autoComplete="off"
            className="slate-text-input-field"
            style={inputStyle}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? name + '-error' : hint ? name + '-hint' : undefined
            }
          />
        </div>

        {error && (
          <div id={name + '-error'} className="slate-text-input-error" role="alert">
            {error}
          </div>
        )}

        {hint && !error && (
          <div id={name + '-hint'} className="slate-text-input-hint">
            {hint}
          </div>
        )}
      </div>
    );
  }
}

SlateTimeInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string, // Expected in HH:MM:SS format
  onChange: PropTypes.func, // Called with HH:MM:SS format
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  fullWidth: PropTypes.bool,
  autoFocus: PropTypes.bool,
  hint: PropTypes.string,
  className: PropTypes.string,
  inputStyle: PropTypes.object,
  containerStyle: PropTypes.object,
};

SlateTimeInput.defaultProps = {
  disabled: false,
  fullWidth: false,
  autoFocus: false,
};

export default SlateTimeInput;
